/**
 * OptionCard Component
 * 
 * A versatile card component for displaying selectable options with different control types.
 * Used across Events and Bulletins for settings, urgency options, and recipient selections.
 * 
 * Features:
 * - Multiple control types: radio buttons, switches, checkboxes
 * - Consistent card styling with hover states
 * - Title and subtitle text support
 * - Disabled state handling
 * - Fixed height for grid alignment (160px)
 * - Active state visual feedback
 * 
 * Control Types:
 * - 'radio': Single selection with circular radio button
 * - 'switch': Toggle switch for on/off options
 * - 'checkbox': Square checkbox for multi-selection
 * 
 * Usage:
 * ```tsx
 * <OptionCard
 *   title="Send Email"
 *   subtitle="Send email notifications to recipients"
 *   value={formData.sendEmail}
 *   onPress={() => toggleOption('sendEmail')}
 *   type="checkbox"
 *   disabled={false}
 * />
 * ```
 */
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Switch } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

interface OptionCardProps {
  title: string;
  subtitle: string;
  value: boolean;
  onPress: () => void;
  type?: 'switch' | 'radio' | 'checkbox';
  disabled?: boolean;
}

export const OptionCard: React.FC<OptionCardProps> = ({
  title,
  subtitle,
  value,
  onPress,
  type = 'switch',
  disabled = false,
}) => {
  return (
    <TouchableOpacity
      style={[
        styles.container,
        value && styles.containerActive,
        disabled && styles.containerDisabled,
      ]}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.7}
    >
      <View style={styles.content}>
        <View style={styles.textContainer}>
          <Text style={[styles.title, disabled && styles.textDisabled]}>
            {title}
          </Text>
          <Text style={[styles.subtitle, disabled && styles.textDisabled]}>
            {subtitle}
          </Text>
        </View>
        
        <View style={styles.controlContainer}>
          {type === 'switch' ? (
            <Switch
              value={value}
              onValueChange={onPress}
              trackColor={{ 
                false: ADMIN_COLORS.BORDER, 
                true: ADMIN_COLORS.PRIMARY 
              }}
              thumbColor={value ? '#FFFFFF' : '#f4f3f4'}
              ios_backgroundColor={ADMIN_COLORS.BORDER}
              disabled={disabled}
            />
          ) : type === 'checkbox' ? (
            <View style={[styles.checkbox, value && styles.checkboxActive]}>
              {value && (
                <SVG_COMPONENTS.CHECK_CIRCLE 
                  width={16} 
                  height={16} 
                  color={ADMIN_COLORS.PRIMARY} 
                />
              )}
            </View>
          ) : (
            <View style={[styles.radio, value && styles.radioActive]}>
              {value && <View style={styles.radioInner} />}
            </View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    marginBottom: 12,
    height: 160,
  },
  containerActive: {
    borderColor: ADMIN_COLORS.PRIMARY,
    backgroundColor: ADMIN_COLORS.PRIMARY + '10',
  },
  containerDisabled: {
    opacity: 0.6,
  },
  content: {
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  textContainer: {
    flex: 1,
    marginRight: 16,
    justifyContent: 'center', // Center text vertically within the container
  },
  title: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    lineHeight: 16,
  },
  textDisabled: {
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  controlContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioActive: {
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  checkboxActive: {
    borderColor: ADMIN_COLORS.PRIMARY,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
});
